import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { SetupFee, SetupFeeWarning } from "../../../../types/onboarding";
import { formatSetupFeeAmount } from "../../../../utils/setupFee";
import { SetupFeeActionResult } from "../../../../hooks/useProspectSetupFee";

interface SetupFeeDialogProps {
  visible: boolean;
  setupFee: SetupFee;
  onHide: () => void;
  onInvoice: () => Promise<SetupFeeActionResult>;
  onWaive: (reason: string) => Promise<SetupFeeActionResult>;
}

const REASON_MAX = 1000;

const WARNING_TEXT: Record<SetupFeeWarning, string> = {
  CONTRACT_PREDATES_FEE_CLAUSE:
    "This customer's contract may predate the setup-fee clause. Check the contract before invoicing.",
  NO_FEE_CONSENT:
    "No setup-fee consent was recorded on the customer's onboarding form.",
};

type Step = "choose" | "confirm-invoice" | "waive";

/**
 * Invoice or waive the onboarding setup fee (CWA-196). Both are final, so each
 * takes a second step: the invoice is confirmed, the waiver needs a reason.
 * Warnings inform the decision and never block it. While an earlier invoice
 * request is still pending, the only safe move is to retry it.
 */
const SetupFeeDialog: React.FC<SetupFeeDialogProps> = ({
  visible,
  setupFee,
  onHide,
  onInvoice,
  onWaive,
}) => {
  const [step, setStep] = useState<Step>("choose");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const retrying = setupFee.status === "INVOICING";
  const trimmed = reason.trim();

  const close = () => {
    setStep("choose");
    setReason("");
    setFailure(null);
    onHide();
  };

  const goTo = (next: Step) => {
    setFailure(null);
    setStep(next);
  };

  const submit = async (action: () => Promise<SetupFeeActionResult>) => {
    setSubmitting(true);
    setFailure(null);
    try {
      const result = await action();
      if (result.ok) close();
      else setFailure(result.message ?? "The setup fee could not be updated.");
    } finally {
      setSubmitting(false);
    }
  };

  const back = (
    <Button label="Back" text onClick={() => goTo("choose")} disabled={submitting} />
  );

  const footer = (() => {
    if (step === "confirm-invoice") {
      return (
        <div>
          {back}
          <Button
            label={retrying ? "Confirm retry" : "Confirm invoice"}
            icon="pi pi-file"
            onClick={() => submit(onInvoice)}
            loading={submitting}
          />
        </div>
      );
    }
    if (step === "waive") {
      return (
        <div>
          {back}
          <Button
            label="Confirm waiver"
            icon="pi pi-ban"
            severity="danger"
            onClick={() => submit(() => onWaive(trimmed))}
            disabled={!trimmed || submitting}
            loading={submitting}
          />
        </div>
      );
    }
    return (
      <div>
        <Button label="Cancel" text onClick={close} />
        {!retrying && (
          <Button
            label="Waive fee"
            icon="pi pi-ban"
            severity="danger"
            outlined
            onClick={() => goTo("waive")}
          />
        )}
        <Button
          label={retrying ? "Retry invoice" : "Create invoice"}
          icon="pi pi-file"
          onClick={() => goTo("confirm-invoice")}
        />
      </div>
    );
  })();

  return (
    <Dialog
      header="Setup fee"
      visible={visible}
      onHide={close}
      footer={footer}
      style={{ width: "34rem" }}
    >
      <p className="mt-0">
        <span className="text-500">Amount: </span>
        <span className="font-semibold">
          {formatSetupFeeAmount(setupFee.gross_amount_cents)} brutto (inkl. 19 % MwSt.)
        </span>
      </p>

      {setupFee.warnings?.map((w) => (
        <Message key={w} severity="warn" className="w-full mb-2" text={WARNING_TEXT[w] ?? w} />
      ))}

      {retrying && step === "choose" && (
        <Message
          severity="info"
          className="w-full mb-2"
          text="An earlier invoice request has not come back from billing. Retrying is safe: a draft that was already created is reused."
        />
      )}

      {step === "confirm-invoice" && (
        <Message
          severity="info"
          className="w-full mb-2"
          text="This creates a DRAFT invoice in Easybill. Finance finalises and sends it there."
        />
      )}

      {step === "waive" && (
        <>
          <label htmlFor="setup-fee-waiver-reason" className="input-label">
            Reason for waiving <span>*</span>
          </label>
          <InputTextarea
            id="setup-fee-waiver-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX}
            rows={4}
            className="w-full mt-2"
            autoResize
          />
          <div className="text-right text-500 text-sm">
            {reason.length} / {REASON_MAX}
          </div>
        </>
      )}

      {failure && <Message severity="error" className="w-full mt-2" text={failure} />}
    </Dialog>
  );
};

export default SetupFeeDialog;
